import React,{ useState, useEffect, useRef, useMemo } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import MaterialTable from "material-table";
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import {
  Modal,
  Grid,
  Typography,
  Box,
  Chip,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Button from "components/CustomButtons/Button.js";
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import AlertDialog from '../components/AlertDialog';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SearchIcon from '@mui/icons-material/Search';

const useStyles = makeStyles((theme) => ({
  heading:{
   marginBottom: 20,
   height: '10%'
  },
  submit1: {
    marginLeft: 30
  },
  submit: {
    backgroundColor: '#2563EB',
    '&:hover': { backgroundColor: '#1D4ED8' },
  },
  submit5: {
    backgroundColor: '#111827',
    '&:hover': { backgroundColor: '#374151' },
  },
  submit3:{
    width: '100%',
    borderRadius: 12,
    marginTop: 8,
    padding: '12px 16px',
    fontWeight: 600,
    textTransform: 'none',
  },
  modal: {
    display: 'flex',
    padding: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 500,
    maxWidth: '95vw',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    padding: theme.spacing(3),
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submit4:{
    backgroundColor: '#111827',
    width: '100%',
    borderRadius: 12,
    marginTop: 8,
    padding: '12px 16px',
    '&:hover': { backgroundColor: '#374151' },
  }
}));

  export default function Users() {
    const { t,i18n } = useTranslation();
    const isRTL = i18n.dir();
    const {
      addUser,
      editUser,
      deleteUser,
      checkUserExists,
      fetchUsers,
      updateLicenseImage
    } = api;
    const settings = useSelector(state => state.settingsdata.settings);
    const [data, setData] = useState([]);
    const users = useSelector(state => state.usersdata.users);
    const auth = useSelector(state => state.auth);
    const dispatch = useDispatch();
    const loaded = useRef(false);
    const classes = useStyles();
    const rootRef = useRef(null);

    useEffect(()=>{
      // Real-time listener - updates instantly when data changes
      dispatch(fetchUsers());
    },[dispatch,fetchUsers]);

    useEffect(()=>{
      if(users){
        setData(users.filter(user => user.usertype ==='customer' && ((user.fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin')|| auth.profile.usertype === 'admin')));
      }else{
        setData([]);
      }
      loaded.current = true;
    },[users,auth.profile]);

    // Handle action menu
    const handleActionClick = (event, rowData) => {
      event.stopPropagation();
      setActionAnchor(event.currentTarget);
      setActionRow(rowData);
    };
    const handleActionClose = () => {
      setActionAnchor(null);
      setActionRow(null);
    };

    // Uber-style compact columns with status dots and action menu
    const columns = [
      { title: t('rider'), field: 'firstName',
        render: rowData => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {rowData.profile_image ? (
              <img alt='Profile' src={rowData.profile_image} style={{width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #F3F4F6'}}/>
            ) : (
              <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonIcon sx={{ color: '#9CA3AF', fontSize: 22 }} />
              </Box>
            )}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                {rowData.firstName} {rowData.lastName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                {settings.AllowCriticalEditsAdmin ? rowData.mobile : "Hidden"}
              </Typography>
            </Box>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('email'), field: 'email', editable:'onAdd',
        render: rowData => (
          <Typography sx={{ fontSize: 14, color: '#374151' }}>
            {settings.AllowCriticalEditsAdmin ? rowData.email : "Hidden"}
          </Typography>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('wallet_balance'), field: 'walletBalance', type:'numeric', editable:'never',
        render: rowData => (
          <Typography sx={{ fontSize: 14, color: '#374151', fontFamily: 'monospace' }}>
            {rowData.walletBalance || 0}
          </Typography>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('account_approve'), field: 'approved', type:'boolean',
        render: rowData => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: rowData.approved ? '#10B981' : '#F59E0B' }} />
            <Typography sx={{ fontSize: 14, color: '#374151' }}>
              {rowData.approved ? (t('approved') || 'Approved') : (t('pending') || 'Pending')}
            </Typography>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('actions'), field: 'action', sorting: false, editable: 'never',
        render: rowData => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t('edit') || 'Edit'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(e, rowData);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: '#F3F4F6',
                  '&:hover': { bgcolor: '#E5E7EB' },
                }}
              >
                <EditIcon sx={{ fontSize: 16, color: '#374151' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('delete') || 'Delete'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (settings.AllowCriticalEditsAdmin) {
                    if (window.confirm(t('delete_message') || 'Are you sure you want to delete?')) {
                      dispatch(deleteUser(rowData.id));
                    }
                  } else {
                    alert(t('demo_mode'));
                  }
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: '#FEE2E2',
                  '&:hover': { bgcolor: '#FECACA' },
                }}
              >
                <DeleteIcon sx={{ fontSize: 16, color: '#DC2626' }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        cellStyle: { padding: '8px 16px', width: 100 },
        headerStyle: { width: 100 },
      },
    ];

    // Detail panel component for expandable row
    const DetailPanel = ({ rowData }) => (
      <Box sx={{ p: 3, bgcolor: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
        <Grid container spacing={3}>
          {/* Personal Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: '#FFFFFF', p: 2, borderRadius: 2, border: '1px solid #E5E7EB' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon sx={{ color: '#2563EB' }} />
                <Typography sx={{ fontWeight: 600, color: '#111827' }}>{t('personal_info') || 'Personal Info'}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <DetailRow label={t('first_name')} value={rowData.firstName || '-'} />
                <DetailRow label={t('last_name')} value={rowData.lastName || '-'} />
                <DetailRow label={t('email')} value={settings.AllowCriticalEditsAdmin ? rowData.email : "Hidden"} />
                <DetailRow label={t('mobile')} value={settings.AllowCriticalEditsAdmin ? rowData.mobile : "Hidden"} />
                <DetailRow label={t('createdAt')} value={rowData.createdAt ? moment(rowData.createdAt).format('lll') : '-'} />
                <DetailRow label={t('verify_id')} value={rowData.verifyId || '-'} />
                <DetailRow label={t('referralId')} value={rowData.referralId || '-'} />
                {rowData.signupViaReferral && <DetailRow label={t('signup_via_referral')} value={rowData.signupViaReferral} />}
              </Box>
              {/* ID Image */}
              {rowData.verifyIdImage && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    component="button"
                    onClick={() => onClick(rowData, 'verifyIdImage')}
                    sx={{ border: '1px solid #E5E7EB', borderRadius: 1, p: 0.5, cursor: 'pointer', bgcolor: 'transparent' }}
                  >
                    <img alt='ID' src={rowData.verifyIdImage} style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 4 }} />
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Wallet & Banking */}
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: '#FFFFFF', p: 2, borderRadius: 2, border: '1px solid #E5E7EB' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalanceIcon sx={{ color: '#2563EB' }} />
                <Typography sx={{ fontWeight: 600, color: '#111827' }}>{t('wallet') || 'Wallet & Banking'}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <DetailRow label={t('wallet_balance')} value={rowData.walletBalance || '0'} />
                {settings.bank_fields !== false && (
                  <>
                    <DetailRow label={t('bankName')} value={rowData.bankName || '-'} />
                    <DetailRow label={t('bankCode')} value={rowData.bankCode || '-'} />
                    <DetailRow label={t('bankAccount')} value={rowData.bankAccount || '-'} />
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );

    // Helper component for detail rows
    const DetailRow = ({ label, value }) => (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#111827', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{value}</Typography>
      </Box>
    );

    const [profileModal, setProfileModal] =  useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [imageData, setImageData] =  useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageType, setImageType] = useState(null);
    const [userData, setUserData] = useState();
    const [loading, setLoading] = useState(false);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });

    // Filter states
    const [filterApproved, setFilterApproved] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Menu anchors for pill filters
    const [approvalAnchor, setApprovalAnchor] = useState(null);

    // Action menu state
    const [actionAnchor, setActionAnchor] = useState(null);
    const [actionRow, setActionRow] = useState(null);

    // Filtered data based on filters and search
    const filteredData = useMemo(() => {
      let result = data;
      if (filterApproved !== 'all') {
        result = result.filter(d => filterApproved === 'approved' ? d.approved : !d.approved);
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        result = result.filter(d =>
          (d.firstName && d.firstName.toLowerCase().includes(query)) ||
          (d.lastName && d.lastName.toLowerCase().includes(query)) ||
          (d.email && d.email.toLowerCase().includes(query)) ||
          (d.mobile && d.mobile.toLowerCase().includes(query))
        );
      }
      return result;
    }, [data, filterApproved, searchQuery]);

    const clearFilters = () => {
      setFilterApproved('all');
      setSearchQuery('');
    };

    // Get filter label for pill buttons
    const getApprovalLabel = () => {
      if (filterApproved === 'all') return t('approval') || 'Approval';
      return filterApproved === 'approved' ? t('approved') || 'Approved' : t('pending') || 'Pending';
    };


  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' })
  };

    const onClick = (rowData,text) => {
      if(text === 'verifyIdImage'){
       setImageType(text);
       setImageData(rowData.verifyIdImage);
     }
     setProfileModal(true);
     setUserData(rowData);
   };

   const handleProfileModal = (e) => {
    setProfileModal(false);
    setSelectedImage(null);
  }

   const handleSetProfileModal = (e) =>{
    e.preventDefault();
    if(selectedImage){
      setLoading(true);
      dispatch(updateLicenseImage(userData.id, selectedImage, imageType));
      setProfileModal(false);
      setSelectedImage(null);
      setTimeout(()=>{
        setLoading(false);
        // Real-time listener will auto-update, no manual refresh needed
      },10000);
    }
    else{
      setCommonAlert({ open: true, msg: t('choose_image_first') })
    }
  }

    return (
      !loaded.current? <CircularLoading/>:
      <div>
        {/* Uber-Style Filter Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            p: 2,
            mb: 2,
            bgcolor: '#FFFFFF',
            borderRadius: 3,
            border: '1px solid #E5E7EB',
            alignItems: 'center',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          {/* Search Input */}
          <TextField
            size="small"
            placeholder={t('search') || 'Search riders...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              minWidth: 220,
              '& .MuiOutlinedInput-root': {
                borderRadius: 50,
                bgcolor: '#F7F7F7',
                '& fieldset': { border: 'none' },
                '&:hover': { bgcolor: '#EFEFEF' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Divider */}
          <Box sx={{ width: 1, height: 24, bgcolor: '#E5E7EB', mx: 1 }} />

          {/* All Pill */}
          <Chip
            label={t('all') || 'All'}
            onClick={clearFilters}
            sx={{
              bgcolor: filterApproved === 'all' ? '#111827' : '#F7F7F7',
              color: filterApproved === 'all' ? '#FFFFFF' : '#374151',
              fontWeight: 500,
              fontSize: 13,
              height: 36,
              borderRadius: 50,
              px: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: filterApproved === 'all' ? '#000000' : '#EFEFEF',
              },
            }}
          />

          {/* Approval Pill */}
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {filterApproved !== 'all' && (
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: filterApproved === 'approved' ? '#10B981' : '#F59E0B' }} />
                )}
                {getApprovalLabel()}
                <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
              </Box>
            }
            onClick={(e) => setApprovalAnchor(e.currentTarget)}
            sx={{
              bgcolor: filterApproved !== 'all' ? '#111827' : '#F7F7F7',
              color: filterApproved !== 'all' ? '#FFFFFF' : '#374151',
              fontWeight: 500,
              fontSize: 13,
              height: 36,
              borderRadius: 50,
              px: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: filterApproved !== 'all' ? '#000000' : '#EFEFEF' },
            }}
          />
          <Menu
            anchorEl={approvalAnchor}
            open={Boolean(approvalAnchor)}
            onClose={() => setApprovalAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 150, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
          >
            <MenuItem onClick={() => { setFilterApproved('all'); setApprovalAnchor(null); }}>
              <Typography sx={{ fontSize: 14 }}>{t('all') || 'All'}</Typography>
            </MenuItem>
            <MenuItem onClick={() => { setFilterApproved('approved'); setApprovalAnchor(null); }}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: '#10B981', mr: 1 }} />
              <Typography sx={{ fontSize: 14 }}>{t('approved') || 'Approved'}</Typography>
            </MenuItem>
            <MenuItem onClick={() => { setFilterApproved('pending'); setApprovalAnchor(null); }}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: '#F59E0B', mr: 1 }} />
              <Typography sx={{ fontSize: 14 }}>{t('pending') || 'Pending'}</Typography>
            </MenuItem>
          </Menu>

          {/* Results Count - Right aligned */}
          <Typography sx={{ ml: 'auto', fontSize: 13, color: '#6B7280' }}>
            {filteredData.length} {t('riders') || 'riders'}
          </Typography>
        </Box>

        <MaterialTable
          icons={TableIcons}
          title={null}
          columns={columns}
          style={{
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
            borderRadius: 12,
            boxShadow: 'none',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
          data={filteredData}
          detailPanel={[
            {
              icon: () => <ExpandMoreIcon sx={{ color: '#6B7280' }} />,
              openIcon: () => <ExpandLessIcon sx={{ color: '#111827' }} />,
              tooltip: t('show_details') || 'Show Details',
              render: rowData => <DetailPanel rowData={rowData} />,
            }
          ]}
          onRowClick={(event, rowData, togglePanel) => {
            // Don't toggle if clicking on action button
            if (event.target.closest('button')) return;
            setSelectedRow(rowData.tableData.id);
            togglePanel();
          }}
          options={{
            ...tableOptions,
            search: false, // Using our own search
            toolbar: false, // Hide toolbar since we have our own filter bar
            showTitle: false,
            exportCsv: (columns, data) => {
              let hArray = [];
              const headerRow = columns.map(col => {
                if (typeof col.title === 'object') {
                  return col.title.props.text;
                }
                hArray.push(col.field);
                return col.title;
              });
              const dataRows = data.map(({ tableData, ...row }) => {
                row.createdAt = new Date(row.createdAt).toLocaleDateString() + ' '+ new Date(row.createdAt).toLocaleTimeString()
                let dArr = [];
                for(let i=0;i< hArray.length; i++) {
                  dArr.push(row[hArray[i]]);
                }
                return Object.values(dArr);
              })
              const { exportDelimiter } = ",";
              const delimiter = exportDelimiter ? exportDelimiter : ",";
              const csvContent = [headerRow, ...dataRows].map(e => e.join(delimiter)).join("\n");
              const csvFileName = 'download.csv';
              downloadCsv(csvContent, csvFileName);
            },
            exportButton: {
              csv: settings.AllowCriticalEditsAdmin,
              pdf: false,
            },
            maxColumnSort: "all_columns",
            rowStyle: rowData => ({
              backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
            }),
          }}
          localization={{ body:{
            addTooltip: (t('add')),
            deleteTooltip: (t('delete')),
            editTooltip: (t('edit')),
            emptyDataSourceMessage: (
              (t('blank_message'))
          ),
            editRow: { 
              deleteText: (t('delete_message')),
              cancelTooltip: (t('cancel')),
              saveTooltip: (t('save')) 
              }, 
            },
            toolbar: {
              searchPlaceholder: (t('search')),
              exportTitle: (t('export')),
            },
            header: {
              actions: (t('actions')) 
          },
          pagination: {
            labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
            firstTooltip: (t('first_page_tooltip')),
            previousTooltip: (t('previous_page_tooltip')),
            nextTooltip: (t('next_page_tooltip')),
            lastTooltip: (t('last_page_tooltip'))
          },
          }}
          editable={{
            onRowAdd: newData =>
              new Promise((resolve,reject) => {
                setTimeout(() => {
                  checkUserExists(newData).then((res) => {
                    if (res.users && res.users.length > 0) {
                      alert(t('user_exists'));
                      reject();
                    }
                    else if(!(newData && newData.firstName)){
                      alert(t('proper_input_name'));
                      reject();
                    }
                    else if(res.error){
                      alert(t('email_or_mobile_issue'));
                      reject();
                    }
                    else if( settings && settings.imageIdApproval && (newData.verifyIdImage === '' || !newData.verifyIdImage)){
                      alert(t('upload_id_details'));
                      reject();
                    }
                    else if( settings && settings.imageIdApproval && !newData.verifyId){
                      alert(t('upload_id_details'));
                      reject();
                    }
                    else{
                      newData['usertype'] = 'customer';
                      newData['createdAt'] = new Date().getTime();
                      const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
                      const reference = [...Array(5)].map(_ => c[~~(Math.random()*c.length)]).join('');
                      newData['referralId'] = reference;
                      delete newData.tableData;
                      let role = auth.profile.usertype;
                      if(role === 'fleetadmin'){
                        newData['fleetadmin'] = auth.profile.uid; 
                      }
                      dispatch(addUser(newData));
                      // Real-time listener will auto-update
                      resolve();
                    }
                  });
                }, 600);
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve,reject) => {
                setTimeout(() => {
                  if(!(newData && newData.firstName)){
                    alert(t('proper_input_name'));
                    reject();
                  } else if( settings && settings.imageIdApproval && (newData.verifyIdImage === '' || !newData.verifyIdImage)){
                    alert(t('upload_id_details'));
                    reject();
                  }
                  else if( settings && settings.imageIdApproval && !newData.verifyId){
                    alert(t('upload_id_details'));
                    reject();
                  }
                  else {
                    resolve();
                    if(newData !== oldData){
                      delete newData.tableData;
                      dispatch(editUser(oldData.id,newData));
                      // Real-time listener will auto-update
                    }
                  }
                }, 600);
              }),
            onRowDelete: oldData =>
              settings.AllowCriticalEditsAdmin?
              new Promise(resolve => {
                setTimeout(() => {
                  resolve();
                  dispatch(deleteUser(oldData.id));
                  // Real-time listener will auto-update
                }, 600);
              })
              :
              new Promise(resolve => {
                setTimeout(() => {
                  resolve();
                  alert(t('demo_mode'));
                }, 600);
              })
              ,
          }}
        />

        {/* Action Menu */}
        <Menu
          anchorEl={actionAnchor}
          open={Boolean(actionAnchor)}
          onClose={handleActionClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 0.5,
              minWidth: 180,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            onClick={() => {
              if (actionRow) {
                handleActionClose();
              }
            }}
            sx={{ py: 1.5, px: 2 }}
          >
            <ListItemIcon>
              <EditIcon sx={{ fontSize: 18, color: '#374151' }} />
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ fontSize: 14 }}>{t('edit') || 'Edit rider'}</Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (actionRow && settings.AllowCriticalEditsAdmin) {
                dispatch(deleteUser(actionRow.id));
              } else {
                alert(t('demo_mode'));
              }
              handleActionClose();
            }}
            sx={{ py: 1.5, px: 2, color: '#DC2626' }}
          >
            <ListItemIcon>
              <DeleteIcon sx={{ fontSize: 18, color: '#DC2626' }} />
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ fontSize: 14, color: '#DC2626' }}>{t('delete') || 'Remove rider'}</Typography>
            </ListItemText>
          </MenuItem>
        </Menu>

        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          open={profileModal}
          onClose={handleProfileModal}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <Grid container spacing={1} className={classes.paper} style={{direction:isRTL==='rtl'?'rtl':'ltr'}}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Typography component="h1" variant="h6">
                        {t('license_image')}
                        <input
                            type="file"
                            style={{marginLeft:10}}
                            name= {t('image')}
                            onChange={(event) => {
                                setSelectedImage(event.target.files[0]);
                            }}
                        />
                  </Typography>
                </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    {selectedImage  && !loading ? 
                      <Tooltip title={t('cancel')}>
                        <CancelIcon onClick={()=>setSelectedImage(null)} style={{ fontSize: 30,backgroundColor:'#1a1a1a',borderRadius:50,color:'#ffffff' }}  />
                      </Tooltip>
                    : null }
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                      {selectedImage ? 
                        <img alt="not fount" height={"200px"} src={URL.createObjectURL(selectedImage)} style={{marginTop:15,marginBottom:20}}/>
                      :
                        <img alt="licimage" height={"200px"} src={imageData} style={{marginTop:10}}/>
                      }
                      <br />
                    </Grid>
                  
            <Grid item xs={12} sm={12} md={12} lg={12} style={{textAlign:isRTL==='rtl'?'right':'left'}}>
              {loading ? 
              <Grid
                container
                spacing={0}
                alignItems="center"
                justify="center"
                style={{ minHeight: '5vh' }} >
                <CircularProgress/>
              </Grid>
              :
              <Grid item xs={12} sm={12} md={12} lg={12} style={{textAlign:isRTL==='rtl'?'right':'left'}}>
              <Button onClick={handleProfileModal} variant="contained" color="danger">
              {t('cancel')}
            </Button> 
              <Button onClick={handleSetProfileModal} variant="contained" color="secondaryButton" style={{marginLeft:10}}>
                {t('save')}
              </Button>
              </Grid>
              }
            </Grid>
          </Grid>
        </Modal>
        <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
      </div>
    );
  }
